import { LiveData, Service } from '@toeverything/infra';

import type { GlobalCache } from '../../storage/providers/global';
import type { WorkspaceService } from '../../workspace';

const DEFAULT_COLLAPSABLE_STATE: Record<string, boolean> = {
  recent: true,
  favorites: false,
  organize: false,
  collections: true,
  tags: true,
  favoritesOld: true,
  migrationFavorites: true,
  others: false,
};

export const NAVIGATION_PANEL_ROOT_SECTIONS = [
  'organize',
  'favorites',
  'migration-favorites',
  'tags',
  'collections',
  'others',
] as const;

export type NavigationPanelRootSection =
  (typeof NAVIGATION_PANEL_ROOT_SECTIONS)[number];

const DEFAULT_ACTIVE_ROOT_SECTION: NavigationPanelRootSection = 'organize';

const isNavigationPanelRootSection = (
  value: string
): value is NavigationPanelRootSection => {
  return (NAVIGATION_PANEL_ROOT_SECTIONS as readonly string[]).includes(value);
};

export const getNavigationPanelRootSectionFallback = (
  sections: readonly NavigationPanelRootSection[]
) => NAVIGATION_PANEL_ROOT_SECTIONS.find(section => sections.includes(section));

export class NavigationPanelService extends Service {
  constructor(
    private readonly globalCache: GlobalCache,
    private readonly workspaceService: WorkspaceService
  ) {
    super();
  }

  private readonly collapsedCache = new Map<string, LiveData<boolean>>();

  private activeRootSectionCache?: LiveData<NavigationPanelRootSection>;

  activeRootSection$() {
    if (!this.activeRootSectionCache) {
      const key = `navigation:${this.workspaceService.workspace.id}:active-root-section`;
      this.activeRootSectionCache = LiveData.from(
        this.globalCache.watch<NavigationPanelRootSection>(key),
        undefined
      ).map(value =>
        value && isNavigationPanelRootSection(value)
          ? value
          : DEFAULT_ACTIVE_ROOT_SECTION
      );
    }

    return this.activeRootSectionCache;
  }

  /**
   * Desktop navigation accordion state. This intentionally does not update the
   * shared collapsed cache, which is still used by the mobile navigation.
   */
  activateRootSection(section: NavigationPanelRootSection) {
    const key = `navigation:${this.workspaceService.workspace.id}:active-root-section`;
    this.globalCache.set(key, section);
  }

  collapsed$(path: string[]) {
    const pathKey = path.join(':');
    const key = `navigation:${this.workspaceService.workspace.id}:${pathKey}`;
    const cached$ = this.collapsedCache.get(key);
    if (!cached$) {
      const liveData$ = LiveData.from(
        this.globalCache.watch<boolean>(key),
        undefined
      ).map(v => v ?? DEFAULT_COLLAPSABLE_STATE[pathKey] ?? true);
      this.collapsedCache.set(key, liveData$);
      return liveData$;
    }
    return cached$;
  }

  setCollapsed(path: string[], collapsed: boolean) {
    const pathKey = path.join(':');
    const key = `navigation:${this.workspaceService.workspace.id}:${pathKey}`;

    this.globalCache.set(key, collapsed);
  }
}
